/**
 * ClassAttendancePanel — the register, as the lesson happens and afterwards.
 *
 * Built from the class roster rather than from arrivals alone, so a pupil who
 * never turned up is listed as absent instead of simply being missing. "Left
 * early" means they were there for less than the school's minimum.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Loader2, RefreshCw, Download } from 'lucide-react';
import { authHeaders as sessionHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Impersonation puts the borrowed token in `admin_token`; a super admin looking
// at a school's register through impersonation must use that one, so it takes
// precedence over the ordinary session.
const authHeaders = () => {
  const impersonating = localStorage.getItem('admin_token');
  return impersonating
    ? { Authorization: `Bearer ${impersonating}` }
    : sessionHeaders();
};

const TONE = {
  present: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  left_early: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  watched_replay: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
  absent: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
};
const WORDS = { present: 'Present', left_early: 'Left early',
  watched_replay: 'Watched the replay', absent: 'Absent' };

const minutes = (m) => (m >= 1 ? `${Math.round(m)} min` : 'under a minute');

export const ClassAttendancePanel = ({ tenantId, sessionId, live }) => {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['class-register', tenantId, sessionId],
    queryFn: () => fetch(
      `${API_URL}/api/school/tenants/${tenantId}/live-classes/${sessionId}/attendance-report`,
      { headers: authHeaders() },
    ).then((r) => (r.ok ? r.json() : null)),
    // While the lesson runs the register keeps itself current.
    refetchInterval: live ? 30000 : false,
  });

  const csv = () => {
    const rows = [['Pupil', 'State', 'Minutes', 'Arrived', 'Left']].concat(
      (data?.attendance || []).map((r) => [
        r.student_name, WORDS[r.state], Math.round(r.minutes),
        r.joined_at || '', r.left_at || '']));
    const blob = new Blob([rows.map((r) => r.join(',')).join('\n')],
      { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `register-${sessionId}.csv`;
    a.click();
  };

  // The whole term, not just this lesson: the registrar's sheet comes from the
  // server because it spans every lesson and pupil, not the rows on screen.
  const termRegister = async () => {
    const since = new Date();
    since.setMonth(since.getMonth() - 4);          // a term, give or take
    const url = `${API_URL}/api/school/tenants/${tenantId}/attendance-export.csv`
      + `?start=${since.toISOString().slice(0, 10)}`;
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('The register could not be exported');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'term-register.csv';
      a.click();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.message);
    }
  };

  return (
    <Card className="p-4" data-testid="class-register">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="h-4 w-4 text-slate-500" />
        <h3 className="text-base font-medium">Register</h3>
        {data && (
          <span className="text-xs text-slate-500" data-testid="register-summary">
            {data.present} present · {data.left_early} left early
            {data.watched_replay > 0 ? ` · ${data.watched_replay} watched the replay` : ''}
            {` · ${data.absent} absent`}
            {live && data.in_room_now ? ` · ${data.in_room_now} here now` : ''}
          </span>
        )}
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => refetch()}
                  data-testid="register-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={csv}
                  disabled={!data?.attendance?.length} data-testid="register-csv-btn">
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={termRegister}
                  title="Every lesson this term, live and replay side by side"
                  data-testid="term-register-btn">
            Term register
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : !data?.attendance?.length ? (
        <p className="text-sm text-slate-500" data-testid="register-empty">
          Nobody has joined this lesson yet. Pupils are added here the moment
          they arrive.
        </p>
      ) : (
        <>
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {data.attendance.map((r) => (
              <li key={r.student_id} className="py-2 flex items-center gap-3"
                  data-testid={`register-row-${r.student_id}`}>
                <span className="flex-1 text-sm truncate">{r.student_name}</span>
                {r.state !== 'absent' && (
                  <span className="text-xs text-slate-500">
                    {minutes(r.minutes)}{r.still_here ? ' · still here' : ''}
                  </span>
                )}
                <Badge variant="secondary" className={TONE[r.state]}>
                  {WORDS[r.state]}
                </Badge>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-3">
            Counted as present from {Math.round(data.minimum_minutes)} minutes of
            a {data.planned_minutes}-minute lesson. A pupil who watched most of
            the recording afterwards is shown as having watched the replay —
            credited, but never recorded as having been in the room.
          </p>
        </>
      )}
    </Card>
  );
};

export default ClassAttendancePanel;
